// A place's card on the Warehouses and Projects lists (owner, 2026-09-14: "easy to see, smooth flow"): who / what it
// is, the one figure that matters (its own assets here out of expected, with a bar that partitions the rest), what
// needs doing as chips — or that nothing does — then footers for the stock count and, for a project location, the
// project. Cards in a row share one height.
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import {
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import type { CountStatus } from '@fleetspoke/core/counts';
import { placeKind } from '@fleetspoke/core/places';
import { tally } from '@fleetspoke/core/sites';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router';
import type { SiteOverview } from '../../data/queries';
import { openCountText, resultText } from '../../lib/counts';
import { placePath } from '../../lib/places';
import { relativeTime } from '../../lib/time';
import { CountStateChip } from '../../pages/CountsPage';
import { PlaceTallyFigures } from './PlaceTally';

/** "Yard" / "Warehouse" for a warehouse; every project or customer site reads "Project location" (P2). */
export function placeTypeLabel(type: SiteOverview['type'], t: (k: string) => string): string {
  return placeKind(type) === 'warehouse'
    ? t(`settings.sites.type_${type}`)
    : t('places.kind_location');
}

// Card footer: the project a location belongs to (a link to it), or — for a location without a project — a
// warning, since arrivals there start no deployment.
function SiteProject({
  site,
  projectName,
  manager,
}: {
  site: SiteOverview;
  projectName: string | undefined;
  manager: boolean;
}) {
  const { t } = useTranslation();
  if (placeKind(site.type) === 'warehouse') return null;
  if (site.project_id && projectName)
    return (
      <CardActions sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider', gap: 0.75 }}>
        <WorkOutlineIcon fontSize="small" color="action" />
        <Typography variant="body2" color="text.secondary">
          {t('sites.project')}:
        </Typography>
        <Link component={RouterLink} to={`/projects/${site.project_id}`} variant="body2">
          {projectName}
        </Link>
      </CardActions>
    );
  if (!site.project_id && manager)
    return (
      <CardActions
        sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider', gap: 0.75, flexWrap: 'wrap' }}
      >
        <WarningAmberIcon fontSize="small" color="warning" />
        <Typography variant="body2" color="warning.main">
          {t('sites.no_project')}
        </Typography>
      </CardActions>
    );
  return null;
}

/** A place's stock count as one chip: the open count (what's left to check), else the schedule state. */
export function CountChip({ count }: { count: CountStatus }) {
  const { t } = useTranslation();
  // same phrase as the state chip: this is count progress, not the presence engine's "missing"
  if (count.open_count)
    return <Chip size="small" color="info" label={openCountText(count.open_count, t)} />;
  if (count.every_days) return <CountStateChip s={count} />;
  return <Chip size="small" variant="outlined" label={t('counts.state_not_scheduled')} />;
}

/** Where a place's stock count opens: the count in progress, else the place's Stock counts tab. */
export const countTarget = (site: SiteOverview, count: CountStatus) =>
  count.open_count ? `/counts/${count.open_count.id}` : placePath(site, 'counts');

// Card footer: the place's stock count (CR_20260914, C10) — its state as a chip, the last result underneath; opens
// the count in progress, else the place's Stock counts tab.
function SiteCountFooter({ site, count }: { site: SiteOverview; count: CountStatus }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const last = count.last_count;
  const result = last ? resultText(last, t) : null;
  const target = countTarget(site, count);
  return (
    <CardActionArea
      onClick={() => void navigate(target)}
      sx={{ borderTop: 1, borderColor: 'divider' }}
      data-testid="card-count"
    >
      <Box sx={{ px: 2, py: 1.25 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <FactCheckOutlinedIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t('counts.title')}
            </Typography>
          </Stack>
          <CountChip count={count} />
        </Stack>
        <Typography
          variant="caption"
          component="p"
          color={result && !result.ok ? 'error.main' : 'text.secondary'}
          sx={{ mt: 0.5, pl: 3.5 }}
        >
          {last && result
            ? t('counts.last_line', {
                when: relativeTime(last.closed_at, i18n.language),
                result: result.label,
              })
            : t('counts.never')}
        </Typography>
      </Box>
    </CardActionArea>
  );
}

export function SiteCard({
  site: s,
  count,
  projectName,
  manager,
  onOpen,
  onEdit,
}: {
  site: SiteOverview;
  count: CountStatus | undefined;
  projectName?: string;
  manager: boolean;
  onOpen(): void;
  onEdit?: () => void;
}) {
  const { t } = useTranslation();
  const figures = tally([s]);
  const gatewayDown = s.gateways > s.gateways_online;
  // "nothing needs attention" is about work waiting on someone, not about assets that are simply away
  const issues = s.awaiting_receipt > 0 || s.missing > 0 || s.not_seen > 0;
  return (
    <Card
      variant="outlined"
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        opacity: s.is_active ? 1 : 0.6,
      }}
      data-testid="site-card"
    >
      <CardActionArea
        onClick={onOpen}
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <CardContent sx={{ pr: onEdit ? 6 : 2, flexGrow: 1 }}>
          <Typography variant="h6" noWrap title={s.name}>
            {s.name}
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ color: 'text.secondary', mb: 2 }}
          >
            <Typography variant="body2" color="inherit">
              {placeTypeLabel(s.type, t)}
            </Typography>
            {s.is_active ? null : <Chip size="small" label={t('sites.inactive')} />}
            <Typography variant="body2" color="inherit" aria-hidden>
              ·
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ color: gatewayDown ? 'error.main' : 'inherit' }}
            >
              <RouterOutlinedIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" color="inherit">
                {s.gateways
                  ? t('sites.gateways', { online: s.gateways_online, total: s.gateways })
                  : t('sites.no_gateway')}
              </Typography>
            </Stack>
          </Stack>

          <PlaceTallyFigures tally={figures} />

          {issues ? null : (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'success.main' }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2" color="inherit">
                {t('sites.all_clear')}
              </Typography>
            </Stack>
          )}
        </CardContent>
      </CardActionArea>
      {/* a sibling of the action area, not inside it: one button may not hold another */}
      {onEdit ? (
        <IconButton
          size="small"
          aria-label={t('settings.sites.edit')}
          onClick={onEdit}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      ) : null}
      {count ? <SiteCountFooter site={s} count={count} /> : null}
      <SiteProject site={s} projectName={projectName} manager={manager} />
    </Card>
  );
}
